export interface RemoteUser {
  id?: string
  name?: string
  email?: string
  phone?: string
  [key: string]: unknown
}

export class RemoteAuthError extends Error {
  code: string

  constructor(code: string) {
    super(code)
    this.name = 'RemoteAuthError'
    this.code = code
  }
}

const AUTH_ERROR_CODES = new Set([
  'AUTH_NOT_CONFIGURED',
  'AUTH_UNAVAILABLE',
  'INVALID_CREDENTIALS',
  'SMS_CODE_INVALID',
  'SMS_CODE_EXPIRED',
])

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

function errorFromPayload(payload: unknown, fallback: string): Error {
  const code = typeof payload === 'object' && payload !== null && typeof (payload as { error?: unknown }).error === 'string'
    ? (payload as { error: string }).error
    : fallback
  if (AUTH_ERROR_CODES.has(code)) return new RemoteAuthError(code)
  return new Error(code)
}

async function postRemoteLogin(path: string, body: Record<string, string>): Promise<RemoteUser> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const payload = await readJson(response)
  if (!response.ok) throw errorFromPayload(payload, 'REMOTE_AUTH_FAILED')
  const user = typeof payload === 'object' && payload !== null ? (payload as { user?: RemoteUser }).user : null
  if (!user) throw new Error('REMOTE_AUTH_FAILED')
  return user
}

export async function getCurrentRemoteUser(): Promise<RemoteUser | null> {
  const response = await fetch('/api/auth/me', { credentials: 'include' })
  const payload = await readJson(response)
  if (response.status === 401) return null
  if (!response.ok) throw errorFromPayload(payload, 'REMOTE_AUTH_FAILED')
  const user = typeof payload === 'object' && payload !== null ? (payload as { user?: RemoteUser }).user : null
  return user ?? null
}

export function loginRemotePassword(account: string, password: string): Promise<RemoteUser> {
  return postRemoteLogin('/api/auth/login/password', { account, password })
}

export function loginRemoteSms(phone: string, code: string): Promise<RemoteUser> {
  return postRemoteLogin('/api/auth/login/sms', { phone, code })
}

export async function logoutRemote(): Promise<void> {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  })
  const payload = await readJson(response)
  if (!response.ok) throw errorFromPayload(payload, 'REMOTE_LOGOUT_FAILED')
}
